import { memo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { BarChart3 } from 'lucide-react'

interface JiraStatusBreakdownProps {
  tasksByStatus: Record<string, number>
  title?: string
}

const statusColors: Record<string, string> = {
  'To Do': 'bg-gray-400',
  'Open': 'bg-gray-400',
  'Backlog': 'bg-gray-400',
  'In Progress': 'bg-blue-500',
  'In Review': 'bg-purple-500',
  'Testing': 'bg-yellow-500',
  'Done': 'bg-green-500',
  'Closed': 'bg-green-600',
  'Resolved': 'bg-green-500',
}

const JiraStatusBreakdown = memo(function JiraStatusBreakdown({ 
  tasksByStatus,
  title = 'Status Breakdown'
}: JiraStatusBreakdownProps) {
  const sortedStatuses = Object.entries(tasksByStatus).sort((a, b) => b[1] - a[1])
  const total = sortedStatuses.reduce((sum, [, count]) => sum + count, 0)

  return (
    <Card variant="compact" padding="sm">
      <CardHeader compact>
        <CardTitle compact className="flex items-center gap-2 text-sm">
          <BarChart3 className="h-4 w-4 text-primary-600" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent compact>
        <div className="space-y-3">
          {sortedStatuses.map(([status, count]) => {
            const percentage = total > 0 ? (count / total) * 100 : 0
            const color = statusColors[status] || 'bg-gray-400'

            return (
              <div key={status}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{status}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {count} ({percentage.toFixed(0)}%)
                  </span>
                </div>
                <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${color} rounded-full transition-all duration-500`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
})

export default JiraStatusBreakdown

